/**
 * Quick Add Panel - Moon Moon Agency
 * Bottom sheet (mobile) / slide-over (desktop) d'ajout rapide au panier depuis une product card.
 * Extrait de mm_card_product.js. Singleton, listeners délégués sur document via init().
 *
 * Dépendances globales : window.log, window.isMobile, window.isDesktop, window.LenisManager,
 * window.ooo (wishlist), window.Shopify, window.mmCartSectionId/mmCartUpdateUI/mmOpenCart,
 * window._qa* (labels Liquid), Splide.
 */

/*********************************************************************************************
 * ⚡ QUICK ADD PANEL — Bottom sheet d'ajout rapide au panier
 */
const QuickAddPanelManager = {
    panel: null,
    activeCard: null,
    selectedOptions: {},
    productData: null,
    _galleryImages: [],
    _livraisonData: {},
    _splide: null,

    // Le nom d'option est TRADUIT par le storefront : une liste FR/EN seule cassait la
    // détection couleur sur DE/IT/NL (ticket 1240z0fpvu5).
    _isColorOption(optionName) {
        const lower = (optionName || '').toLowerCase();
        return ['couleur', 'color', 'colour', 'farbe', 'colore', 'kleur'].includes(lower);
    },

    _create() {
        const tpl = document.getElementById('mm-qap-shell-template');
        if (!tpl) { window.log('error', 'QuickAdd', 'Template mm-qap-shell-template introuvable'); return; }
        const el = tpl.content.cloneNode(true).firstElementChild;
        document.body.appendChild(el);
        this.panel = el;

        el.querySelector('.mm-qap__overlay').addEventListener('click', () => this.close());
        el.querySelectorAll('.mm-qap__close').forEach(btn => btn.addEventListener('click', (e) => { e.preventDefault(); this.close(); }));
        document.addEventListener('keydown', (e) => { if (e.key === 'Escape') this.close(); });

        el.querySelector('.mm-qap__body').addEventListener('click', (e) => {
            /* Swatch couleur */
            const swatch = e.target.closest('.mm-qap__swatch:not(.mm-qap__swatch--unavailable)');
            if (swatch) {
                this._selectOption(parseInt(swatch.dataset.optionIndex, 10), swatch.dataset.value);
                return;
            }
            /* Bouton taille */
            const sizeBtn = e.target.closest('.mm-qap__size-btn:not(.mm-qap__size-btn--unavailable)');
            if (sizeBtn) {
                this._selectOption(parseInt(sizeBtn.dataset.optionIndex, 10), sizeBtn.dataset.value);
            }
        });

        /* Pas de gestion du message no-preview au survol : il n'apparaît qu'à la sélection
           d'une couleur sans image (géré dans _showImageForVariant, appelé au clic).
           Le changement d'images se fait aussi au clic — on ne re-render PAS au survol
           (le remount Splide décale le layout et rend les swatches incliquables). */

        el.querySelector('.mm-qap__atc').addEventListener('click', () => this._addToCart());
        el.querySelector('.mm-qap__wishlist-add').addEventListener('click', () => this._toggleWishlist());
    },

    /**
     * Resout l'id de variant realement exploitable par l'app Wishlist Power.
     *
     * L'app resout le variant via /products/{handle}.js, plafonne a 250 variants.
     * Au-dela, le variant choisi est absent de ce JSON : l'app l'ignore et enregistre
     * le produit avec variant_id null. Passer l'id dans ce cas rend containsProduct
     * toujours false (chaque clic re-ajoute, le libelle ne bascule jamais).
     * On retombe alors sur null pour rester aligne avec ce que l'app stocke.
     */
    async _wishlistVariantId(handle, variantId) {
        if (!variantId) return null;
        try {
            const res = await fetch(`${window.Shopify?.routes?.root || '/'}products/${handle}.js`);
            if (!res.ok) return null;
            const data = await res.json();
            const known = (data?.variants || []).some((v) => v.id === Number(variantId));
            return known ? variantId : null;
        } catch (_) {
            return null;
        }
    },

    /**
     * Toggle current variant in Wishlist Power.
     * Reads selected variant from QAP state and uses window.ooo.wishlist.functions.
     */
    async _toggleWishlist() {
        const handle = this.activeCard?.dataset?.wishlistHandle
            || this.activeCard?.dataset?.productHandle
            || null;
        if (!handle) return;

        const variant = this._getCurrentVariant();
        const variantId = variant?.id || null;

        const fns = window.ooo?.wishlist?.functions;
        if (!fns?.isWishlistAccessible?.()) {
            window.log('warn', '❤ [QAP_WISHLIST]', 'Wishlist not accessible');
            return;
        }

        const btn = this.panel?.querySelector('.mm-qap__wishlist-add');
        if (!btn) return;
        btn.disabled = true;
        btn.classList.add('mm-loading');

        try {
            const wishlistId = await this._wishlistVariantId(handle, variantId);
            const isInWishlist = await fns.containsProduct(handle, wishlistId);
            if (isInWishlist) {
                await fns.removeProduct(handle, wishlistId);
            } else {
                await fns.addProduct(handle, wishlistId);
            }

            if (!isInWishlist) {
                /* Ajout : on ferme le panel (parité avec l'add-to-cart) */
                this.close();
                window.log('info', '❤ [QAP_WISHLIST]', 'Added to wishlist', { variantId });
            } else {
                /* Retrait : le panel reste ouvert, on rafraîchit juste le label */
                await this._refreshWishlistButton();
            }
        } catch (e) {
            window.log('error', '❤ [QAP_WISHLIST]', 'Toggle failed', { error: e.message });
        } finally {
            btn.disabled = false;
            btn.classList.remove('mm-loading');
        }
    },

    /**
     * Refresh wishlist button label based on current selected variant.
     */
    async _refreshWishlistButton() {
        const btn = this.panel?.querySelector('.mm-qap__wishlist-add');
        const label = btn?.querySelector('.mm-qap__wishlist-add-label');
        if (!btn || !label) return;

        const handle = this.activeCard?.dataset?.wishlistHandle
            || this.activeCard?.dataset?.productHandle;
        const fns = window.ooo?.wishlist?.functions;
        if (!handle || !fns?.isWishlistAccessible?.()) return;

        const variant = this._getCurrentVariant();
        try {
            const wishlistId = await this._wishlistVariantId(handle, variant?.id || null);
            const inWishlist = await fns.containsProduct(handle, wishlistId);
            btn.classList.toggle('mm-qap__wishlist-add--active', !!inWishlist);
            label.textContent = inWishlist
                ? (window._qaWishlistRemoveLabel || 'Retirer des favoris')
                : (window._qaWishlistAddLabel || 'Ajouter aux favoris');
        } catch (_) { /* ignore */ }
    },

    /**
     * Récupère les variants d'un produit au format MM { options:[names], variants:[{id, option1..3, available, price, compare_price?}] }.
     * Lazy-load : plus inliné dans chaque card. Ordre : <script> inline (compat) → cache → fetch /products/{handle}.js.
     * Retourne null si indisponible (le panel reste utilisable en mode mono-variante / lien produit).
     */
    async _fetchVariantsData(card) {
        /* Fallback : script inline encore présent (compat ascendante) */
        const inline = card.querySelector('.mm-pcard__variants-data');
        if (inline) {
            const parsed = window.parseJSON(inline);
            if (parsed) return parsed;
        }

        const handle = card.dataset.wishlistHandle || card.dataset.productHandle;
        if (!handle) return null;

        this._variantsCache = this._variantsCache || new Map();
        if (this._variantsCache.has(handle)) return this._variantsCache.get(handle);

        try {
            const res = await fetch(`/products/${handle}.js`);
            if (!res.ok) throw new Error(`HTTP ${res.status}`);
            const product = await res.json();

            const data = {
                options: (product.options || []).map(o => o.name || o),
                variants: (product.variants || []).map(v => {
                    const out = {
                        id: v.id,
                        option1: v.option1 || '',
                        option2: v.option2 || '',
                        option3: v.option3 || '',
                        available: v.available,
                        price: window.formatMoney(v.price),
                    };
                    if (v.compare_at_price && v.compare_at_price > v.price) {
                        out.compare_price = window.formatMoney(v.compare_at_price);
                    }
                    return out;
                }),
            };

            /* Produits >250 variants : /products/{handle}.js est cappé à 250 → sans complément,
               les couleurs des pages 2+ sont marquées indisponibles (_updateAvailability ne
               trouve aucun variant) et inachetables au QAP. Compléter depuis la section paginée
               (fetch partagé avec mmFetchColorPages via mmFetchVariantPages). */
            const declared = parseInt(card.dataset.variantsCount, 10);
            if (declared > 250 || (product.variants || []).length === 250) {
                const pages = await window.mmFetchVariantPages(handle, declared > 250 ? declared : null);
                const seen = new Set(data.variants.map(v => v.id));
                for (const page of pages) {
                    for (const v of (page.variants || [])) {
                        if (seen.has(v.id)) continue;
                        seen.add(v.id);
                        const opts = v.options || [];
                        const out = {
                            id: v.id,
                            option1: opts[0] || '',
                            option2: opts[1] || '',
                            option3: opts[2] || '',
                            available: v.available,
                            price: window.formatMoney(v.price),
                        };
                        if (v.compare_at_price && v.compare_at_price > v.price) {
                            out.compare_price = window.formatMoney(v.compare_at_price);
                        }
                        data.variants.push(out);
                    }
                }
            }

            this._variantsCache.set(handle, data);
            return data;
        } catch (e) {
            window.log('warn', '⚡ [QUICK_ADD]', 'Failed to fetch variants data', { handle, error: e.message });
            return null;
        }
    },

    /* Données couleur complètes (jusqu'à 14) chargées à l'ouverture via l'endpoint section,
       au lieu d'être rendues dans la grille (coût metaobject × N produits = timeout).
       Calqué sur _fetchVariantsData : fallback inline (6 couleurs) → cache Map → fetch. */
    async _fetchColorsData(card) {
        const handle = card.dataset.wishlistHandle || card.dataset.productHandle;
        if (!handle) return this._colorsFromInline(card);

        this._colorsCache = this._colorsCache || new Map();
        if (this._colorsCache.has(handle)) return this._colorsCache.get(handle);

        try {
            const res = await fetch(`/products/${handle}?section_id=mm-pcard-colors&_v=${window.mmAssetsV || ''}`);
            if (!res.ok) throw new Error(`HTTP ${res.status}`);
            const doc = new DOMParser().parseFromString(await res.text(), 'text/html');
            let colors = window.parseJSON(doc.querySelector('.mm-pcard__colors-data'), []);
            const livraison = window.parseJSON(doc.querySelector('.mm-pcard__livraison-data'), {});
            const meta = window.parseJSON(doc.querySelector('.mm-pcard__colors-meta'), {});
            let available = meta.available ?? meta.total ?? colors.length;

            // Produits > 250 variants : l'endpoint ne voit que les 250 premiers. Compléter avec
            // les couleurs des pages 2+ (mm-product-variants-page), puis RE-TRIER l'ensemble par
            // stock physique cumulé décroissant (tri stock GLOBAL, même règle que le SSR).
            const pagesMeta = window.parseJSON(doc.querySelector('.mm-pcard-colors-pages'), {});
            if (pagesMeta.variantsCount > 250) {
                const extra = await window.mmFetchColorPages(handle, pagesMeta.variantsCount);
                const byName = new Map(colors.map(c => [c.name, c]));
                for (const c of extra) {
                    const dup = byName.get(c.name);
                    if (dup) {
                        // Couleur à cheval sur la frontière des 250 : fusionner les agrégats
                        // (son stock/dispo page 1 est partiel, le reste vit dans les pages 2+).
                        dup.stock = (dup.stock || 0) + (c.stock || 0);
                        if (c.available && !dup.available) { dup.available = true; available++; }
                        const liv = livraison[String(dup.id)];
                        if (liv && c.livraison) {
                            liv.qty = (liv.qty || 0) + (c.livraison.qty || 0);
                            if (c.livraison.policy === 'continue') liv.policy = 'continue';
                            if (c.livraison.onDemand) liv.onDemand = true;
                        }
                        continue;
                    }
                    byName.set(c.name, c);
                    colors.push(c);
                    if (c.available) available++;
                    // Livraison indexée sur l'id du variant représentatif (même clé que le SSR).
                    if (c.livraison) livraison[String(c.id)] = c.livraison;
                }
                colors.sort((a, b) => {
                    const sa = a.stock || 0, sb = b.stock || 0;
                    if ((sa > 0) !== (sb > 0)) return sa > 0 ? -1 : 1; // en stock d'abord
                    return sb - sa; // stock décroissant ; sort stable → ordre SSR conservé à égalité
                });
            }

            // "Voir les N couleurs" = couleurs ACHETABLES (available), pas le total brut.
            const data = { colors, livraison, total: available };
            this._colorsCache.set(handle, data);
            return data;
        } catch (e) {
            window.log('warn', '⚡ [QUICK_ADD]', 'Failed to fetch colors data', { handle, error: e.message });
            return this._colorsFromInline(card);
        }
    },

    /* Fallback : données couleur inline de la card (limitées à 6, mais le total reste exact
       via .mm-pcard__colors-meta). Utilisé si pas de handle ou si le fetch échoue. */
    _colorsFromInline(card) {
        const colors = window.parseJSON(card.querySelector('.mm-pcard__colors-data'), []);
        const livraison = window.parseJSON(card.querySelector('.mm-pcard__livraison-data'), {});
        const meta = window.parseJSON(card.querySelector('.mm-pcard__colors-meta'), {});
        return { colors, livraison, total: meta.available ?? meta.total ?? colors.length };
    },

    async open(card, mode = 'atc') {
        if (!this.panel) this._create();
        if (!this.panel) return;

        this.mode = mode;
        this.panel.dataset.qapMode = mode;

        const img = card.querySelector('.mm-pcard__img--main');
        const title = card.querySelector('.mm-pcard__title');
        const desc = card.querySelector('.mm-pcard__material');
        const priceCurrent = card.querySelector('.mm-pcard__price');
        const productLink = card.querySelector('.mm-pcard__link');
        const form = card.querySelector('[data-type="add-to-cart-form"]');

        if (!form) return;

        /* Header title + footer swap based on mode */
        const headLabel = this.panel.querySelector('.mm-qap__head-label');
        const footerAtc = this.panel.querySelector('[data-qap-footer-atc]');
        const footerWishlist = this.panel.querySelector('[data-qap-footer-wishlist]');
        if (mode === 'wishlist') {
            if (headLabel) headLabel.textContent = window._qaWishlistPanelTitle || 'Ajouter aux favoris';
            if (footerAtc) footerAtc.hidden = true;
            if (footerWishlist) footerWishlist.hidden = false;
        } else {
            if (headLabel) headLabel.textContent = window._qaAddToCartLabel || 'Ajouter au panier';
            if (footerAtc) footerAtc.hidden = false;
            if (footerWishlist) footerWishlist.hidden = true;
        }

        const data = await this._fetchVariantsData(card);
        const colorsData = await this._fetchColorsData(card);

        this.activeCard = card;
        this._colorsData = colorsData.colors;
        this._colorsTotal = colorsData.total;
        this._colorsImgMap = null;
        this.productData = data && data.variants ? { variants: data.variants, options: data.options } : null;
        this.selectedOptions = {};

        /* Nettoyer le DOM scroll-mode si le panel a été utilisé en scroll avant */
        const scrollRoot = this.panel.querySelector('.mm-qap__scroll-root');
        if (scrollRoot) scrollRoot.remove();
        this.panel.classList.remove('mm-qap--scroll');
        const drawer = this.panel.querySelector('.mm-qap__drawer');
        if (drawer) drawer.style.cssText = '';

        /* Image */
        const panelImg = this.panel.querySelector('.mm-qap__img');
        if (img && panelImg) { panelImg.src = img.src; panelImg.alt = img.alt || ''; }

        /* Lien produit sur le bloc header + CTA "Voir le produit" (les 2 footers) */
        const productHref = productLink?.href || '#';
        const productEl = this.panel.querySelector('.mm-qap__product');
        if (productEl) productEl.href = productHref;
        this.panel.querySelectorAll('.mm-qap__view-product').forEach((el) => {
            el.href = productHref;
        });

        /* Meta */
        const nameEl = this.panel.querySelector('.mm-qap__name');
        const descEl = this.panel.querySelector('.mm-qap__desc');
        if (nameEl) nameEl.textContent = title?.textContent?.trim() || '';
        if (descEl) descEl.textContent = desc?.textContent?.trim() || '';

        /* Prix dans le CTA */
        const atcLabel = this.panel.querySelector('.mm-qap__atc-label');
        const atcPrice = this.panel.querySelector('.mm-qap__atc-price');
        const atcCompare = this.panel.querySelector('.mm-qap__atc-compare-price');
        if (atcLabel) atcLabel.textContent = window._qaAddToCartLabel || 'Ajouter au panier';
        if (atcPrice) {
            const priceEl = priceCurrent?.cloneNode(true);
            priceEl?.querySelector('.mm-pcard__price-compare')?.remove();
            atcPrice.textContent = priceEl?.textContent?.trim() || '';
        }
        if (atcCompare) {
            const compareEl = priceCurrent?.querySelector('.mm-pcard__price-compare');
            atcCompare.textContent = compareEl?.textContent?.trim() || '';
            atcCompare.style.display = compareEl ? '' : 'none';
        }

        /* Sections options */
        this._renderOptions(card);

        /* Livraison — map variantId → {qty,policy,onDemand} (chargé via _fetchColorsData) */
        this._livraisonData = colorsData.livraison;

        /* Livraison — variante active */
        const activeVId = card.querySelector('[data-variant-id-input]')?.value;
        if (activeVId) this._updateLivraison(activeVId);

        /* Galerie images — pas en mode wishlist (panneau minimaliste).
           À l'ouverture : TOUTES les images produit (galerie par défaut). Le switch sur les
           images de la couleur ne se fait qu'au clic d'une pastille (cf. _selectOption non
           silent). La 1ère pastille reste active visuellement mais ne filtre pas la galerie. */
        if (mode !== 'wishlist') {
            this._initImages(card);
        }

        this.panel.setAttribute('aria-hidden', 'false');
        this.panel.classList.add('mm-qap--open');
        document.body.classList.add('mm-overflow-hidden');
        /* Stopper Lenis : overflow:hidden seul ne bloque pas le smooth scroll JS */
        window.LenisManager?.stop();
        this.panel.querySelector('.mm-qap__close').focus();

        /* Sync wishlist button state for the card's default variant */
        this._refreshWishlistButton();

        window.log('info', '⚡ [QUICK_ADD]', 'Panel opened');
    },

    close() {
        if (!this.panel) return;
        if (this._arcRafId) { cancelAnimationFrame(this._arcRafId); this._arcRafId = null; }
        this.panel.setAttribute('aria-hidden', 'true');
        this.panel.classList.remove('mm-qap--open');
        document.body.classList.remove('mm-overflow-hidden');
        /* Relancer Lenis + recalcul des dimensions */
        window.LenisManager?.resume();
        requestAnimationFrame(() => window.LenisManager?.resize());
        this.activeCard = null;
        this.productData = null;
        this.selectedOptions = {};
    },

    _renderOptions(card) {
        const body = this.panel.querySelector('.mm-qap__body');
        body.innerHTML = '';

        if (!this.productData || !this.productData.options) return;

        const productLink = card.querySelector('.mm-pcard__link')?.href || '#';
        const findSizeLabel = window._qaFindSizeLabel || 'Trouver ma taille';
        const seeMoreLabel = window._qaSeeMoreLabel || 'Voir plus';

        /* Si aucune option couleur dans le produit, afficher le swatch image par défaut */
        const hasColorOption = this.productData.options.some(o => this._isColorOption(o));
        if (!hasColorOption) {
            const colorsData = this._colorsData || [];

            if (colorsData.length > 0) {
                const swatchesEl = document.createElement('div');
                swatchesEl.className = 'mm-qap__swatches';
                const swatchesInner = document.createElement('div');
                swatchesInner.className = 'mm-qap__swatches-inner';
                /* Couleurs "à la commande" (onDemand) masquées des pastilles. */
                colorsData.filter(c => !c.onDemand).forEach(colorInfo => {
                    const swatch = document.createElement('button');
                    swatch.type = 'button';
                    swatch.className = 'mm-qap__swatch mm-qap__swatch--active';
                    swatch.title = colorInfo.name;
                    if (colorInfo.style) swatch.style.cssText = colorInfo.style;
                    swatchesInner.appendChild(swatch);
                });
                swatchesEl.appendChild(swatchesInner);
                body.appendChild(swatchesEl);
            }
        }

        this.productData.options.forEach((option, idx) => {
            const isColor = this._isColorOption(option);
            const values = [...new Set(this.productData.variants.map(v => v[`option${idx + 1}`]))];

            const section = document.createElement('div');
            section.className = 'mm-qap__section';
            section.dataset.optionIndex = idx;

            /* Header section — uniquement pour les couleurs */
            let header = null;
            if (isColor) {
                header = document.createElement('div');
                header.className = 'mm-qap__section-header';

                const meta = document.createElement('div');
                meta.className = 'mm-qap__section-meta';

                const labelEl = document.createElement('span');
                labelEl.className = 'mm-qap__section-label';
                meta.appendChild(labelEl);

                const livraisonEl = document.createElement('span');
                livraisonEl.className = 'mm-qap__livraison mm-pdp-livraison text-xs-caps';
                meta.appendChild(livraisonEl);

                header.appendChild(meta);
                section.appendChild(header);
            }

            if (isColor) {
                /* Section couleurs : données chargées via _fetchColorsData (endpoint, jusqu'à 14) */
                const MAX_SWATCHES = 14;
                const colorsData = this._colorsData || [];

                const swatchesEl = document.createElement('div');
                swatchesEl.className = 'mm-qap__swatches';

                const swatchesInner = document.createElement('div');
                swatchesInner.className = 'mm-qap__swatches-inner';

                /* Règle "couleur = axe primaire" (identique au SSR mm-pcard-colors-data L220) :
                   pastille seulement si couleur ACHETABLE ; les "à la commande" (onDemand) sont
                   masquées ici mais gardées dans _colorsData (le color panel de la PDP les affiche).
                   Sans le filtre `available`, les produits > 250 variants réinjectaient les couleurs
                   OOS pures des pages 2+ (mmFetchColorPages n'en filtre aucune) → pastilles fantômes
                   absentes de la PDP. */
                colorsData.filter(c => c.available && !c.onDemand).slice(0, MAX_SWATCHES).forEach(colorInfo => {
                    const swatch = document.createElement('button');
                    swatch.type = 'button';
                    swatch.className = 'mm-qap__swatch';
                    swatch.dataset.optionIndex = idx;
                    swatch.dataset.value = colorInfo.name;
                    if (colorInfo.id) swatch.dataset.variantId = colorInfo.id;
                    swatch.title = colorInfo.name;
                    if (colorInfo.style) swatch.style.cssText = colorInfo.style;
                    swatchesInner.appendChild(swatch);
                });

                swatchesEl.appendChild(swatchesInner);

                const colorsTotal = this._colorsTotal || colorsData.length;
                if (colorsTotal > MAX_SWATCHES && header) {
                    const seeMore = document.createElement('a');
                    seeMore.href = productLink;
                    seeMore.className = 'mm-qap__section-link';
                    /* "Voir les N couleurs" (N = total réel) ; fallback label générique */
                    const allLabel = window._qaSeeAllColorsLabel;
                    seeMore.textContent = allLabel ? allLabel.replace('__COUNT__', colorsTotal) : seeMoreLabel;
                    header.appendChild(seeMore);
                }

                section.appendChild(swatchesEl);

            } else {
                /* Section tailles : boutons inline */
                const isSize = (option || '').toLowerCase().includes('taille') || (option || '').toLowerCase().includes('size');

                const sizesEl = document.createElement('div');
                sizesEl.className = 'mm-qap__sizes';

                const sizesInner = document.createElement('div');
                sizesInner.className = 'mm-qap__sizes-inner';

                values.forEach(value => {
                    const btn = document.createElement('button');
                    btn.type = 'button';
                    btn.className = 'mm-qap__size-btn';
                    btn.dataset.optionIndex = idx;
                    btn.dataset.value = value;
                    btn.textContent = value;
                    sizesInner.appendChild(btn);
                });

                sizesEl.appendChild(sizesInner);

                if (isSize) {
                    const sizeHeader = document.createElement('div');
                    sizeHeader.className = 'mm-qap__section-header mm-qap__section-header--sizes';
                    const findSize = document.createElement('a');
                    findSize.href = productLink;
                    findSize.className = 'mm-qap__section-link';
                    findSize.textContent = findSizeLabel;
                    sizeHeader.appendChild(findSize);
                    section.appendChild(sizeHeader);
                }

                section.appendChild(sizesEl);
            }

            body.appendChild(section);
        });

        /* Info produit (ex: "Taille serrée, choisissez la taille en dessous") */
        const infoProduit = card.dataset.qaInfo;
        if (infoProduit) {
            const infoEl = document.createElement('p');
            infoEl.className = 'mm-qap__info-produit';
            infoEl.textContent = infoProduit;
            body.appendChild(infoEl);
        }

        /* Pré-sélectionner la variante active = variante native de la card (data-variant-id-input
           = selected_or_first_available_variant, y compris pour la COULEUR). Un choix explicite
           sur la card (swatch --active, ex via filtres collection) reste prioritaire. */
        const activeVariantId = card.querySelector('[data-variant-id-input]')?.value;
        if (activeVariantId && this.productData.variants) {
            const activeVariant = this.productData.variants.find(v => String(v.id) === String(activeVariantId));
            if (activeVariant) {
                const cardChosenColor = card.querySelector('.mm-pcard__swatch--active')?.title || null;
                this.productData.options.forEach((option, idx) => {
                    let val = activeVariant[`option${idx + 1}`];
                    if (this._isColorOption(option) && cardChosenColor) {
                        val = cardChosenColor;
                    }
                    if (val) this._selectOption(idx, val, true);
                });
            }
        }
    },

    _selectOption(optionIndex, value, silent = false) {
        this.selectedOptions[`option${optionIndex + 1}`] = value;

        const body = this.panel.querySelector('.mm-qap__body');
        const section = body.querySelector(`.mm-qap__section[data-option-index="${optionIndex}"]`);
        if (!section) return;

        /* Mettre à jour label actif dans header */
        const labelEl = section.querySelector('.mm-qap__section-label');
        if (labelEl) labelEl.textContent = value;

        /* Mettre à jour état actif des swatches */
        section.querySelectorAll('.mm-qap__swatch').forEach(s => {
            s.classList.toggle('mm-qap__swatch--active', s.dataset.value === value);
        });

        /* Mettre à jour état actif des tailles */
        section.querySelectorAll('.mm-qap__size-btn').forEach(btn => {
            btn.classList.toggle('mm-qap__size-btn--active', btn.dataset.value === value);
        });

        this._updateAvailability();
        this._updateCTAPrice();

        /* Mettre à jour la livraison + (hors silent) basculer la galerie sur les images de la
           couleur. En silent = pré-sélection initiale (1ère pastille) : on NE touche PAS la
           galerie → elle garde TOUTES les images produit. Le switch sur les images de la
           couleur ne se fait qu'au clic utilisateur d'une pastille. */
        if (this._isColorOption(this.productData?.options?.[optionIndex])) {
            const variant = this._getCurrentVariant();
            if (variant) {
                if (!silent) this._showImageForVariant(variant.id, false);
                this._updateLivraison(variant.id);
            }
        }

        /* Refresh wishlist state for current variant */
        this._refreshWishlistButton();

        if (!silent) {
            window.log('info', '⚡ [QUICK_ADD]', 'Option selected', { optionIndex, value });
        }
    },

    _updateCTAPrice() {
        const variant = this._getCurrentVariant();
        if (!variant) return;
        const priceEl = this.panel.querySelector('.mm-qap__atc-price');
        const compareEl = this.panel.querySelector('.mm-qap__atc-compare-price');
        if (priceEl && variant.price) priceEl.textContent = variant.price;
        if (compareEl) {
            compareEl.textContent = variant.compare_price || '';
            compareEl.style.display = variant.compare_price ? '' : 'none';
        }
    },

    _updateAvailability() {
        if (!this.productData) return;

        const body = this.panel.querySelector('.mm-qap__body');
        const colorIdx = this.productData.options?.findIndex(o => this._isColorOption(o));

        body.querySelectorAll('.mm-qap__section').forEach(section => {
            const idx = parseInt(section.dataset.optionIndex, 10);
            const isColorSection = idx === colorIdx;

            section.querySelectorAll('.mm-qap__swatch, .mm-qap__size-btn').forEach(el => {
                const optKey = `option${idx + 1}`;
                let available;
                if (isColorSection) {
                    // Couleur = axe primaire : grisée seulement si AUCUNE taille de ce coloris n'est
                    // achetable (pas de croisement avec les autres options sélectionnées). Sinon un
                    // coloris dispo dans une autre taille deviendrait non cliquable. Le clic bascule
                    // ensuite la taille sur la 1ère dispo (voir _switchSizeIfActiveHidden).
                    available = this.productData.variants.some(v => v[optKey] === el.dataset.value && v.available);
                } else {
                    // Taille = croisée avec la couleur (et autres options) courante.
                    const testOptions = { ...this.selectedOptions, [optKey]: el.dataset.value };
                    available = this.productData.variants.some(v =>
                        Object.keys(testOptions).every(key => v[key] === testOptions[key]) && v.available
                    );
                }

                const unavailableClass = el.classList.contains('mm-qap__swatch')
                    ? 'mm-qap__swatch--unavailable'
                    : 'mm-qap__size-btn--unavailable';

                el.classList.toggle(unavailableClass, !available);
                el.disabled = !available;
            });
        });

        this._switchColorIfActiveHidden();
        this._switchSizeIfActiveHidden();
    },

    /* Après un changement de COULEUR : si la taille active n'existe pas pour la nouvelle couleur
       (grisée), basculer sur la 1ère taille dispo du coloris. Ne touche jamais la couleur. */
    _switchSizeIfActiveHidden() {
        const colorIdx = this.productData?.options?.findIndex(o => this._isColorOption(o));
        const body = this.panel.querySelector('.mm-qap__body');
        if (!body) return;

        body.querySelectorAll('.mm-qap__section').forEach(section => {
            const idx = parseInt(section.dataset.optionIndex, 10);
            if (idx === colorIdx) return; // sections tailles uniquement

            const activeBtn = section.querySelector('.mm-qap__size-btn--active');
            if (!activeBtn || !activeBtn.classList.contains('mm-qap__size-btn--unavailable')) return;

            const firstAvailable = section.querySelector('.mm-qap__size-btn:not(.mm-qap__size-btn--unavailable)');
            if (!firstAvailable) return;

            this._selectOption(idx, firstAvailable.dataset.value);
        });
    },

    /* Si la couleur active est indispo (grisée) pour la taille sélectionnée, basculer sur
       la 1ère couleur dispo. Garde anti-boucle : ne rien faire si aucune couleur dispo. */
    _switchColorIfActiveHidden() {
        const colorIdx = this.productData?.options?.findIndex(o => this._isColorOption(o));
        if (colorIdx === undefined || colorIdx < 0) return;

        const section = this.panel.querySelector(`.mm-qap__section[data-option-index="${colorIdx}"]`);
        if (!section) return;

        const activeSwatch = section.querySelector('.mm-qap__swatch--active');
        if (!activeSwatch || !activeSwatch.classList.contains('mm-qap__swatch--unavailable')) return;

        const firstVisible = section.querySelector('.mm-qap__swatch:not(.mm-qap__swatch--unavailable)');
        if (!firstVisible) return;

        this._selectOption(colorIdx, firstVisible.dataset.value);
    },

    _getVariantIdForColor(colorValue) {
        if (!this.productData) return null;
        const colorOptIdx = this.productData.options.findIndex(o => this._isColorOption(o));
        if (colorOptIdx === -1) return null;
        const key = `option${colorOptIdx + 1}`;
        const testOptions = { ...this.selectedOptions, [key]: colorValue };
        const variant = this.productData.variants.find(v =>
            Object.keys(testOptions).every(k => v[k] === testOptions[k])
        );
        return variant?.id || null;
    },

    _getCurrentVariant() {
        if (!this.productData) return null;
        return this.productData.variants.find(v => {
            return Object.keys(this.selectedOptions).every(key => v[key] === this.selectedOptions[key]);
        }) || null;
    },

    async _addToCart() {
        const variant = this._getCurrentVariant();
        window.log('info', '⚡ [QUICK_ADD]', 'Add to cart attempt', {
            selectedOptions: this.selectedOptions,
            variant: variant ? { id: variant.id, available: variant.available } : null,
            allVariants: this.productData?.variants?.map(v => ({ id: v.id, option1: v.option1, option2: v.option2, option3: v.option3, available: v.available }))
        });
        if (!variant || !variant.available) return;

        const btn = this.panel.querySelector('.mm-qap__atc');
        btn.classList.add('mm-loading');
        btn.disabled = true;

        try {
            const sectionId = window.mmCartSectionId?.() || '';
            const item = { id: variant.id, quantity: 1 };

            // Line item property "Délai" — même texte que le badge livraison (checkout + commande).
            // _getLivraisonEntry résout par couleur (le map est indexé par variante représentative,
            // pas par taille). Vide (rupture) → pas de property.
            const delaiText = this._livraisonText(this._getLivraisonEntry(variant.id));
            const delaiName = window._qaDeliveryPropertyName;
            if (delaiText && delaiName) {
                item.properties = { [delaiName]: delaiText };
            }

            const body = { items: [item] };
            if (sectionId) body.sections = sectionId;

            const response = await fetch('/cart/add.js', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
                body: JSON.stringify(body)
            });
            if (!response.ok) throw new Error(`HTTP ${response.status}`);
            const data = await response.json();

            window.mmCartUpdateUI?.(data);

            document.dispatchEvent(new CustomEvent('cart:add-to-cart-success', {
                bubbles: true,
                detail: { data: { variantId: variant.id, quantity: 1, response: data } }
            }));

            this.close();
            window.mmOpenCart?.();
            window.log('info', '⚡ [QUICK_ADD]', 'Added to cart', { variantId: variant.id });
        } catch (e) {
            window.log('error', '⚡ [QUICK_ADD]', 'Add to cart failed', { error: e.message });
        } finally {
            btn.classList.remove('mm-loading');
            btn.disabled = false;
        }
    },

    _updateLivraison(variantId) {
        const el = this.panel?.querySelector('.mm-qap__livraison');
        if (!el) return;
        const entry = this._getLivraisonEntry(variantId);
        const text = this._livraisonText(entry);
        el.textContent = text || '';
        el.style.display = text ? '' : 'none';
    },

    /* Résout l'entrée livraison {qty,policy,onDemand} pour un variant donné.
       _livraisonData est indexé par la variante REPRÉSENTATIVE de chaque couleur
       (1er variant, cf. mm-pcard-colors-data), pas par la variante taille+couleur exacte.
       Un lookup direct par variantId rate donc dès qu'on n'est pas sur la 1ère taille.
       On résout via le nom de couleur → id représentatif (_colorsData), avec fallback direct. */
    _getLivraisonEntry(variantId) {
        const data = this._livraisonData || {};
        // 1. Lookup direct (cas 1ère taille / variante représentative)
        let entry = data[variantId] || data[String(variantId)];
        if (entry) return entry;
        // 2. Résolution par couleur : nom → id représentatif → entrée
        const colorName = this._colorNameForVariant(variantId);
        if (colorName) {
            const rep = (this._colorsData || []).find(c => this._normalizeColor(c.name) === this._normalizeColor(colorName));
            if (rep && rep.id != null) entry = data[rep.id] || data[String(rep.id)];
        }
        return entry || '';
    },

    /* Dérive le texte de livraison depuis l'objet {qty, policy, onDemand} (format colors-data,
       même logique que mm_color_panel._groupColors). Garde un fallback string (ancien format). */
    _livraisonText(entry) {
        if (!entry) return '';
        if (typeof entry === 'string') {
            const prefix = !entry.toLowerCase().includes('commande') ? (window._qaDeliveryLabel || 'Delivered') + ' ' : '';
            return prefix + entry;
        }
        const deliveryIn = window._qaDeliveryInLabel || 'Livraison en';
        if (entry.onDemand) return window._qaDeliveryOnDemandLabel || 'À la commande';
        if ((entry.qty || 0) <= 0 && entry.policy === 'continue') return `${deliveryIn} ${window._qaDeliveryRestockValue || '3/4 semaines'}`;
        return `${deliveryIn} ${window._qaDefaultDeliveryValue || '3/5 jours'}`;
    },

    _getCardImages(card) {
        const mediaEl = card.querySelector('[data-pcard-images]');
        let images = [];
        if (mediaEl?.dataset.pcardImages) {
            try { images = JSON.parse(mediaEl.dataset.pcardImages); } catch (e) { /* noop */ }
        }
        if (!images.length) {
            const mainImg = card.querySelector('.mm-pcard__img--main');
            if (mainImg?.src) images = [mainImg.src];
        }
        return images;
    },

    _initImages(card) {
        this._galleryImages = this._getCardImages(card);
        this._renderGallery(this._galleryImages);
    },

    _renderGallery(images) {
        /* Détruire l'instance Splide précédente */
        if (this._splide) {
            try { this._splide.destroy(); } catch (e) { /* noop */ }
            this._splide = null;
        }

        const gallery = this.panel.querySelector('.mm-qap__gallery');
        const list = gallery.querySelector('.splide__list');
        const bar = gallery.querySelector('.mm-qap__gallery-bar');

        list.innerHTML = '';
        bar.style.width = '0%';

        if (!images.length) return;

        const single = images.length === 1;
        /* Masquer la barre de progression quand une seule image */
        gallery.classList.toggle('mm-qap__gallery--single', single);

        images.forEach((src, i) => {
            const li = document.createElement('li');
            li.className = 'splide__slide mm-qap__gallery-slide';
            const img = document.createElement('img');
            img.className = 'mm-qap__gallery-img';
            img.src = src;
            img.alt = '';
            img.loading = i === 0 ? 'eager' : 'lazy';
            img.decoding = 'async';
            li.appendChild(img);
            list.appendChild(li);
        });

        const config = single
            ? { pagination: false, arrows: false, drag: false, rewind: false }
            : { type: 'slide', perPage: 1, gap: 0, pagination: false, arrows: false, rewind: false, speed: 400, easing: 'cubic-bezier(0.32, 0.72, 0, 1)' };

        this._splide = new Splide(gallery, config).mount();

        /* Progress bar synchronisée avec Splide */
        this._splide.on('moved', (newIndex) => {
            const end = this._splide.Components.Controller.getEnd() + 1;
            bar.style.width = `${Math.min(((newIndex + 1) / end) * 100, 100)}%`;
        });

        /* Init progress */
        if (!single) {
            const end = images.length;
            bar.style.width = `${(1 / end) * 100}%`;
        } else {
            bar.style.width = '100%';
        }
    },

    _normalizeColor(str) {
        return (str || '').toString().trim().toLowerCase();
    },

    /* Couleur (option) d'un variant courant — pour résoudre les images par couleur
       et non par id (l'id change avec la taille, mais les images sont par couleur). */
    _colorNameForVariant(variantId) {
        if (!this.productData) return null;
        const variant = this.productData.variants.find(v => String(v.id) === String(variantId));
        if (!variant) return null;
        const colorIdx = this.productData.options.findIndex(o => this._isColorOption(o));
        if (colorIdx < 0) return null;
        return variant[`option${colorIdx + 1}`] || null;
    },

    _ensureColorsMap() {
        if (this._colorsImgMap) return;
        this._colorsImgMap = {};       /* indexé par id ET par nom de couleur */
        this._coloriImgsMap = {};
        const data = this._colorsData || [];
        data.forEach(c => {
            const imgs = Array.isArray(c.coloriImgs) ? c.coloriImgs : [];
            if (c.id != null) {
                this._colorsImgMap[String(c.id)] = c.img || '';
                this._coloriImgsMap[String(c.id)] = imgs;
            }
            const key = 'name:' + this._normalizeColor(c.name);
            this._colorsImgMap[key] = c.img || '';
            this._coloriImgsMap[key] = imgs;
        });
    },

    /* Récupère l'URL d'image d'une couleur (champ "img").
       Résout par id exact, sinon par nom de couleur du variant (id varie avec la taille). */
    _colorImageFor(variantId) {
        if (!this.activeCard) return null;
        this._ensureColorsMap();
        if (this._colorsImgMap[String(variantId)]) return this._colorsImgMap[String(variantId)];
        const name = this._colorNameForVariant(variantId);
        return (name && this._colorsImgMap['name:' + this._normalizeColor(name)]) || null;
    },

    /* Images dédiées de la couleur (custom.colori → "coloriImgs"), résolues par id ou par nom. */
    _coloriImagesFor(variantId) {
        if (!this.activeCard) return [];
        this._ensureColorsMap();
        const byId = this._coloriImgsMap[String(variantId)];
        if (byId && byId.length) return byId;
        const name = this._colorNameForVariant(variantId);
        return (name && this._coloriImgsMap['name:' + this._normalizeColor(name)]) || [];
    },

    _showImageForVariant(variantId, silent = false) {
        if (!this.activeCard) return;
        /* Image de la couleur : lue dans colors-data (toutes les couleurs, pas seulement
           les 6 swatches rendus dans la card). Fallback sur le swatch card si besoin. */
        const swatchImg = this._colorImageFor(variantId)
            || this.activeCard.querySelector(`.mm-pcard__swatch[data-variant-id="${variantId}"]`)?.dataset.swatchImg
            || null;
        const noPreview = this.panel.querySelector('.mm-qap__no-preview');

        /* Couleur sans image associée : afficher le message (au survol comme à la sélection)
           et garder la galerie actuelle inchangée — on ne montre jamais les images du produit
           (qui sont des photos d'autres coloris, pas des images génériques).
           silent = pré-sélection initiale → pas de message, on laisse la galerie produit
           par défaut (rendue ensuite par _initImages). */
        if (!swatchImg) {
            if (noPreview && !silent) noPreview.classList.add('mm-qap__no-preview--visible');
            return;
        }
        if (noPreview) noPreview.classList.remove('mm-qap__no-preview--visible');

        /* Miniature mm-qap__img : toujours l'image du variant (jamais les images colori) */
        const thumbImg = this.panel.querySelector('.mm-qap__img');
        if (thumbImg) thumbImg.src = swatchImg;

        /* Slider : images dédiées de la couleur (custom.colori) si présentes,
           sinon uniquement l'image de la couleur (jamais les autres coloris du produit). */
        const coloriImgs = this._coloriImagesFor(variantId);
        const images = coloriImgs.length ? coloriImgs : [swatchImg];

        /* Pas de re-render si la galerie montre déjà ces images en tête */
        if (this._galleryImages[0] && this._galleryImages[0].split('?')[0] === images[0].split('?')[0]) return;

        this._galleryImages = images;
        this._renderGalleryWithFade(images);
    },

    /* Fondu court sur la galerie pendant le remount (transition douce au changement de couleur) */
    _renderGalleryWithFade(images) {
        const gallery = this.panel.querySelector('.mm-qap__gallery');
        if (!gallery) { this._renderGallery(images); return; }
        gallery.classList.add('mm-qap__gallery--fading');
        clearTimeout(this._galleryFadeTimer);
        this._galleryFadeTimer = setTimeout(() => {
            this._renderGallery(images);
            requestAnimationFrame(() => gallery.classList.remove('mm-qap__gallery--fading'));
        }, 180);
    },

    /* ── SCROLL MODE ─────────────────────────────────────────────────────── */

    _isScrollMode(card) {
        const btn = card.querySelector('[data-quick-add-panel]');
        return btn?.hasAttribute('data-scroll-mode') && window.isMobile();
    },

    async _openScrollMode(card) {
        if (!this.panel) this._create();
        if (!this.panel) return;

        const title = card.querySelector('.mm-pcard__title');
        const mainImg = card.querySelector('.mm-pcard__img--main');
        const form = card.querySelector('[data-type="add-to-cart-form"]');

        if (!form) return;

        const variantsData = await this._fetchVariantsData(card);
        const colorsResult = await this._fetchColorsData(card);
        const colorsData = colorsResult.colors;

        this.activeCard = card;
        this._colorsData = colorsData;
        this._colorsTotal = colorsResult.total;
        this._colorsImgMap = null;
        this.productData = variantsData && variantsData.variants ? variantsData : null;
        this.selectedOptions = {};

        this.panel.classList.add('mm-qap--scroll');

        /* Vider et reconstruire le drawer en mode scroll */
        const drawer = this.panel.querySelector('.mm-qap__drawer');
        let scrollRoot = drawer.querySelector('.mm-qap__scroll-root');
        if (scrollRoot) scrollRoot.remove();

        scrollRoot = document.createElement('div');
        scrollRoot.className = 'mm-qap__scroll-root';
        scrollRoot.style.cssText = 'display:flex;flex-direction:column;height:100%;';

        /* 1. Header */
        const head = document.getElementById('mm-qap-head-scroll-template').content.cloneNode(true).firstElementChild;
        head.querySelector('.mm-qap__head-scroll-title').textContent = title?.textContent?.trim() || '';
        head.querySelector('.mm-qap__close').addEventListener('click', () => this.close());
        scrollRoot.appendChild(head);

        /* 2. Canvas — teinte pixel-perfect uniquement sur les pixels non-transparents */
        const imageWrap = document.createElement('div');
        imageWrap.className = 'mm-qap__image-scroll';

        const imgSrc = card.dataset.scrollImg || mainImg?.src || '';
        this._scrollImgSrc = imgSrc;

        const colorLayer = document.createElement('canvas');
        colorLayer.className = 'mm-qap__scroll-color';

        /* Badge livraison — positionné en absolu dans imageWrap (chargé via _fetchColorsData) */
        this._livraisonData = colorsResult.livraison;

        const badge = document.createElement('span');
        badge.className = 'mm-qap__livraison mm-pdp-livraison text-xs-caps';
        badge.style.display = 'none';

        imageWrap.appendChild(colorLayer);
        imageWrap.appendChild(badge);
        scrollRoot.appendChild(imageWrap);

        /* Référence pour _selectScrollColor */
        this._scrollColorLayer = colorLayer;

        /* 4. Arc swatches */
        const arcWrap = document.createElement('div');
        arcWrap.className = 'mm-qap__arc-wrap';
        const arcInner = document.createElement('div');
        arcInner.className = 'mm-qap__arc-inner';
        arcWrap.appendChild(arcInner);
        scrollRoot.appendChild(arcWrap);

        /* 5. Label couleur */
        const colorLabel = document.createElement('p');
        colorLabel.className = 'mm-qap__color-label';
        scrollRoot.appendChild(colorLabel);

        /* 6. Tailles */
        const sizesSection = document.createElement('div');
        sizesSection.style.cssText = 'padding: 0 var(--margin-3) var(--margin-2); flex-shrink:0;';
        scrollRoot.appendChild(sizesSection);

        drawer.appendChild(scrollRoot);

        /* Footer — hors du scroll-root, collé en bas du drawer */
        let footer = drawer.querySelector('.mm-qap__footer');
        if (!footer) {
            footer = document.createElement('div');
            footer.className = 'mm-qap__footer';
            drawer.appendChild(footer);
        }
        footer.replaceChildren(
            document.getElementById('mm-qap-footer-scroll-template').content.cloneNode(true)
        );
        footer.querySelector('.mm-qap__atc').addEventListener('click', () => this._addToCart());

        /* Rendre l'arc */
        this._renderArc(arcWrap, arcInner, colorsData, colorLayer, colorLabel, badge);

        /* Tailles */
        this._renderSizesScroll(sizesSection);

        /* Pré-sélectionner */
        if (colorsData.length > 0) {
            this._selectScrollColor(0, colorsData, colorLayer, colorLabel, badge);
        }

        this.panel.setAttribute('aria-hidden', 'false');
        this.panel.classList.add('mm-qap--open');
        document.body.classList.add('mm-overflow-hidden');
    },

    _renderArc(arcWrap, arcInner, colorsData, colorLayer, colorLabel, badge) {
        arcInner.innerHTML = '';
        if (!colorsData.length) return;

        const SWATCH_MAX = 38;
        const SWATCH_MIN = 24;
        const GAP = 10;
        const ARC_DEPTH = 30;
        const LERP_SPEED = 0.08;
        const SNAP_THRESH = 0.3;
        const containerW = arcWrap.offsetWidth || window.innerWidth;
        const n = colorsData.length;
        const swatchW = SWATCH_MAX + GAP;
        const totalW = n * swatchW;
        arcInner.style.width = Math.max(totalW, containerW) + 'px';

        let offset = containerW / 2 - swatchW / 2;
        let targetOffset = offset;
        let activeIndex = 0;
        if (this._arcRafId) cancelAnimationFrame(this._arcRafId);
        this._arcRafId = null;

        const swatchEls = colorsData.map((c, i) => {
            const el = document.createElement('div');
            el.className = 'mm-qap__arc-swatch';
            el.style.backgroundColor = c.hex || c.style?.match(/#[0-9a-fA-F]{3,8}/)?.[0] || '#ccc';
            el.dataset.index = i;
            arcInner.appendChild(el);
            return el;
        });

        let containerX = arcWrap.getBoundingClientRect().width / 2 || containerW / 2;

        const clampOffset = (v) => {
            const min = containerX - (n - 0.5) * swatchW;
            const max = containerX - swatchW / 2;
            return Math.max(min, Math.min(max, v));
        };

        const position = (off) => {
            swatchEls.forEach((el, i) => {
                const swatchCenterX = off + i * swatchW + swatchW / 2;
                const distFromCenter = Math.abs(swatchCenterX - containerX);
                const maxDist = containerX;
                const t = Math.min(distFromCenter / maxDist, 1);
                const ease = 1 - Math.pow(t, 1.35);
                const size = SWATCH_MIN + (SWATCH_MAX - SWATCH_MIN) * ease;
                const yOffset = ARC_DEPTH * (1 - ease);
                const halfSize = size / 2;
                el.style.width = size + 'px';
                el.style.height = size + 'px';
                el.style.transform = `scale(${ease * 0.35 + 0.65})`;
                el.style.opacity = (0.3 + ease * 0.7).toFixed(3);
                el.style.left = (off + i * swatchW + (SWATCH_MAX - size) / 2) + 'px';
                el.style.top = (50 - halfSize + yOffset) + 'px';
            });

            let closestIdx = 0, closestDist = Infinity;
            swatchEls.forEach((_, i) => {
                const swatchCenterX = off + i * swatchW + swatchW / 2;
                const dist = Math.abs(swatchCenterX - containerX);
                if (dist < closestDist) { closestDist = dist; closestIdx = i; }
            });
            return closestIdx;
        };

        const applyActive = (idx) => {
            if (idx === activeIndex) return;
            activeIndex = idx;
            swatchEls.forEach((el, i) => el.classList.toggle('mm-qap__arc-swatch--active', i === idx));
            if (navigator.vibrate) navigator.vibrate(8);
            this._selectScrollColor(idx, colorsData, colorLayer, colorLabel, badge);
        };

        const loop = () => {
            offset += (targetOffset - offset) * LERP_SPEED;
            if (Math.abs(offset - targetOffset) < SNAP_THRESH) offset = targetOffset;
            const nearest = position(offset);
            applyActive(nearest);
            this._arcRafId = requestAnimationFrame(loop);
        };

        /* Position initiale sans lerp */
        position(offset);
        swatchEls[0]?.classList.add('mm-qap__arc-swatch--active');
        this._arcRafId = requestAnimationFrame(loop);

        /* Drag avec velocity/momentum */
        let isDragging = false;
        let startX = 0, startOffset = 0;
        let lastX = 0, lastT = 0, velocity = 0;

        const onStart = (clientX) => {
            isDragging = true;
            startX = clientX;
            startOffset = offset;
            lastX = clientX;
            lastT = performance.now();
            velocity = 0;
            containerX = arcWrap.getBoundingClientRect().width / 2;
        };

        const onMove = (clientX) => {
            if (!isDragging) return;
            const delta = clientX - startX;
            targetOffset = clampOffset(startOffset + delta);

            const now = performance.now();
            const dt = Math.max(now - lastT, 1);
            velocity = (clientX - lastX) / dt;
            lastX = clientX;
            lastT = now;
        };

        const onEnd = () => {
            if (!isDragging) return;
            isDragging = false;
            /* Snap avec momentum : projeter selon velocity puis snapper sur le swatch le plus proche */
            const projected = targetOffset + velocity * 150;
            targetOffset = clampOffset(Math.round((projected - containerX + swatchW / 2) / swatchW) * swatchW + containerX - swatchW / 2);
        };

        arcWrap.addEventListener('pointerdown', (e) => { arcWrap.setPointerCapture(e.pointerId); onStart(e.clientX); }, { passive: true });
        arcWrap.addEventListener('pointermove', (e) => onMove(e.clientX), { passive: true });
        arcWrap.addEventListener('pointerup', onEnd, { passive: true });
        arcWrap.addEventListener('pointercancel', onEnd, { passive: true });

        /* Tap direct sur swatch — anime vers ce swatch */
        arcInner.addEventListener('click', (e) => {
            const swatch = e.target.closest('.mm-qap__arc-swatch');
            if (!swatch) return;
            const idx = parseInt(swatch.dataset.index, 10);
            targetOffset = clampOffset(containerX - idx * swatchW - swatchW / 2);
            applyActive(idx);
        });
    },

    _tintCanvas(canvas, hex) {
        const src = this._scrollImgSrc;
        if (!src) return;

        const img = new Image();
        img.crossOrigin = 'anonymous';
        img.onload = () => {
            canvas.width = img.naturalWidth;
            canvas.height = img.naturalHeight;
            const ctx = canvas.getContext('2d');

            ctx.drawImage(img, 0, 0);

            if (hex) {
                ctx.globalCompositeOperation = 'source-atop';
                ctx.globalAlpha = 0.9;
                ctx.fillStyle = hex;
                ctx.fillRect(0, 0, canvas.width, canvas.height);

                ctx.globalCompositeOperation = 'multiply';
                ctx.globalAlpha = 1;
                ctx.drawImage(img, 0, 0);
            }
        };
        img.src = src;
    },

    _selectScrollColor(idx, colorsData, colorLayer, colorLabel, badge) {
        const color = colorsData[idx];
        if (!color) return;

        /* Teinte canvas — pixels non-transparents uniquement */
        const hex = color.hex || color.style?.match(/#[0-9a-fA-F]{3,8}/)?.[0] || '';
        this._tintCanvas(colorLayer, hex);

        /* Label couleur */
        colorLabel.textContent = color.name || '';

        /* Livraison */
        const variantId = color.id;
        if (variantId) {
            const liv = this._livraisonData?.[variantId] || this._livraisonData?.[String(variantId)] || '';
            const text = this._livraisonText(liv);
            badge.textContent = text || '';
            badge.style.display = text ? '' : 'none';
        }

        /* Pré-sélectionner l'option couleur */
        if (this.productData) {
            const colorOptIdx = this.productData.options?.findIndex(o => this._isColorOption(o));
            if (colorOptIdx !== undefined && colorOptIdx >= 0) {
                this.selectedOptions[`option${colorOptIdx + 1}`] = color.name;
                /* Mettre à jour prix + dispo tailles */
                this._updateScrollSizesAvailability();
                this._updateCTAPrice();
            }
        }
    },

    _renderSizesScroll(container) {
        container.innerHTML = '';
        if (!this.productData) return;

        this.productData.options.forEach((option, idx) => {
            if (this._isColorOption(option)) return;

            const values = [...new Set(this.productData.variants.map(v => v[`option${idx + 1}`]))];
            const sizesInner = document.createElement('div');
            sizesInner.className = 'mm-qap__sizes-inner';
            sizesInner.dataset.optionIndex = idx;

            values.forEach(value => {
                const btn = document.createElement('button');
                btn.type = 'button';
                btn.className = 'mm-qap__size-btn';
                btn.dataset.optionIndex = idx;
                btn.dataset.value = value;
                btn.textContent = value;
                sizesInner.appendChild(btn);
            });

            /* Pré-sélectionner la première taille disponible */
            const firstAvailable = sizesInner.querySelector('.mm-qap__size-btn:not([disabled])');
            if (firstAvailable) {
                firstAvailable.classList.add('mm-qap__size-btn--active');
                this.selectedOptions[`option${idx + 1}`] = firstAvailable.dataset.value;
            }

            container.appendChild(sizesInner);
        });

        container.addEventListener('click', (e) => {
            const btn = e.target.closest('.mm-qap__size-btn:not(.mm-qap__size-btn--unavailable)');
            if (!btn) return;
            const idx = parseInt(btn.dataset.optionIndex, 10);
            this.selectedOptions[`option${idx + 1}`] = btn.dataset.value;
            container.querySelectorAll(`.mm-qap__sizes-inner[data-option-index="${idx}"] .mm-qap__size-btn`).forEach(b => {
                b.classList.toggle('mm-qap__size-btn--active', b.dataset.value === btn.dataset.value);
            });
            this._updateCTAPrice();
        });
    },

    _updateScrollSizesAvailability() {
        if (!this.productData || !this.activeCard) return;
        const scrollRoot = this.panel.querySelector('.mm-qap__scroll-root');
        if (!scrollRoot) return;

        scrollRoot.querySelectorAll('.mm-qap__sizes-inner').forEach(inner => {
            const idx = parseInt(inner.dataset.optionIndex, 10);
            inner.querySelectorAll('.mm-qap__size-btn').forEach(btn => {
                const testOptions = { ...this.selectedOptions };
                testOptions[`option${idx + 1}`] = btn.dataset.value;
                const available = this.productData.variants.some(v =>
                    Object.keys(testOptions).every(key => v[key] === testOptions[key]) && v.available
                );
                btn.classList.toggle('mm-qap__size-btn--unavailable', !available);
                btn.disabled = !available;
            });

            // Couleur = axe primaire : après un changement de couleur, la taille active peut ne
            // plus exister pour ce coloris. Basculer sur la 1ère taille dispo (met à jour l'état
            // actif + selectedOptions) pour que la couleur reste sélectionnable/achetable.
            const activeBtn = inner.querySelector('.mm-qap__size-btn--active');
            if (activeBtn && activeBtn.classList.contains('mm-qap__size-btn--unavailable')) {
                const firstAvailable = inner.querySelector('.mm-qap__size-btn:not(.mm-qap__size-btn--unavailable)');
                if (firstAvailable) {
                    inner.querySelectorAll('.mm-qap__size-btn').forEach(b =>
                        b.classList.toggle('mm-qap__size-btn--active', b === firstAvailable));
                    this.selectedOptions[`option${idx + 1}`] = firstAvailable.dataset.value;
                }
            }
        });
    },

    init() {
        /* Trigger mobile — bottom sheet */
        document.addEventListener('click', (e) => {
            const trigger = e.target.closest('.mm-pcard__quick-add[data-quick-add-panel]');
            if (!trigger) return;
            e.preventDefault();
            e.stopPropagation();
            const card = trigger.closest('.mm-pcard');
            if (!card) return;
            if (this._isScrollMode(card)) {
                this._openScrollMode(card);
            } else {
                this.open(card);
            }
        });

        /* Trigger desktop — slide-over depuis la droite */
        document.addEventListener('click', (e) => {
            const trigger = e.target.closest('[data-desktop-quick-add]');
            if (!trigger || !window.isDesktop()) return;
            e.preventDefault();
            e.stopPropagation();
            const card = trigger.closest('.mm-pcard');
            if (card) this.open(card);
        });
    }
};

/*********************************************************************************************
 * 🚀 AUTO-INIT
 */
if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => QuickAddPanelManager.init());
} else {
    QuickAddPanelManager.init();
}

window.QuickAddPanelManager = QuickAddPanelManager;
